import { useState, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import Loader from "@/components/ui/Loader";

/**
 * Componente que protege rutas verificando si el usuario tiene una suscripción activa.
 * EXCEPCIÓN: agency_admin SIEMPRE pasa directo (su bloqueo es por estado de agencia, no suscripción).
 */
export default function SubscriptionGuard() {
  const { user, profile, loading: authLoading, isAuthenticated, logout } = useAuth();
  const { isSubscribed, subscriptionData, loading: subscriptionLoading } = useSubscription();
  const location = useLocation();
  const [profileTimeout, setProfileTimeout] = useState(false);

  // Timeout para profile: si después de 5s auth terminó pero profile sigue null, algo falló
  useEffect(() => {
    if (!authLoading && user && !profile) {
      const timer = setTimeout(() => setProfileTimeout(true), 12000); // Extended to 12s to tolerate slow profile fetches during bulk operations
      return () => clearTimeout(timer);
    }
    setProfileTimeout(false);
  }, [authLoading, user, profile]);

  // =======================================================================
  // FASE 1: Esperar autenticación
  // =======================================================================
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader size={40} />
      </div>
    );
  }

  // No autenticado → login
  if (!user || !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Perfil no cargado aún → esperar (con timeout de seguridad)
  if (!profile) {
    if (profileTimeout) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
          <p className="text-muted-foreground">No se pudo cargar tu perfil. Por favor intenta de nuevo.</p>
          <div className="flex gap-3">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              Reintentar
            </button>
            <button
              onClick={() => logout()}
              className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader size={40} />
      </div>
    );
  }

  // =======================================================================
  // FASE 2: FAST PATH para agency_admin y dhm_supervisor
  // Las agencias y supervisores DHM NO pasan por verificación de suscripción.
  // =======================================================================
  if (profile.role === 'agency_admin' || profile.role === 'dhm_supervisor') {
    return <Outlet />;
  }

  // =======================================================================
  // FASE 3: Para agentes — verificar suscripción
  // =======================================================================
  if (subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader size={40} />
      </div>
    );
  }

  const status = subscriptionData?.status || subscriptionData?.subscription_status;
  const isValidSubscription = status === 'active' || status === 'trialing';

  if (isSubscribed && isValidSubscription) {
    return <Outlet />;
  }

  // Agente sin suscripción: permitir /agent/dashboard (tiene SubscriptionLock interno)
  if (location.pathname === '/agent/dashboard') {
    return <Outlet />;
  }

  // Cualquier otra ruta sin suscripción → redirigir
  sessionStorage.setItem('redirectAfterSubscription', location.pathname + location.search);
  return <Navigate to="/agent/dashboard" replace />;
}
